"use client";

import { useEffect, useRef, useState } from "react";
import { handleBlogScroll } from "@/app/utils/scroll";

interface Heading {
  text: string;
  id: string;
}

interface BlogStickyTOCProps {
  headings: Heading[];
}

export default function BlogStickyTOC({ headings }: BlogStickyTOCProps) {
  const [activeId, setActiveId] = useState<string | null>(
    headings[0]?.id ?? null,
  );
  const listRef = useRef<HTMLUListElement>(null);

  // Track scroll position and update the active heading.
  // Uses "last heading above threshold" so the active item is always
  // the section the user is currently reading, even mid-section.
  useEffect(() => {
    if (headings.length === 0) return;

    function updateActive() {
      const threshold = 140; // px from top of viewport
      let current: string | null = null;

      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= threshold) {
          current = h.id;
        } else {
          break;
        }
      }

      setActiveId(current ?? headings[0].id);
    }

    updateActive();

    let raf = 0;
    const handler = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateActive);
    };

    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, [headings]);

  // Keep the active item visible inside the TOC sidebar (when scrollable).
  // We adjust ONLY the TOC's own scroll container so the page/window is never affected.
  useEffect(() => {
    if (!activeId || !listRef.current) return;
    const item = listRef.current.querySelector<HTMLElement>(
      `[data-toc-id="${activeId}"]`,
    );
    if (!item) return;

    const scrollContainer = item.closest("aside") as HTMLElement | null;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    if (itemRect.top < containerRect.top) {
      scrollContainer.scrollTop -= containerRect.top - itemRect.top;
    } else if (itemRect.bottom > containerRect.bottom) {
      scrollContainer.scrollTop += itemRect.bottom - containerRect.bottom;
    }
  }, [activeId]);

  if (headings.length === 0) return null;

  return (
    <nav aria-label="Table of contents">
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-4">
        On this page
      </p>
      <ul ref={listRef} className="flex flex-col">
        {headings.map((h, i) => {
          const active = activeId === h.id;
          return (
            <li key={h.id} data-toc-id={h.id}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleBlogScroll(h.id);
                }}
                className={`group w-full text-left flex items-baseline gap-2.5 py-1.5 pl-4 border-l-2 text-sm transition-colors ${
                  active
                    ? "border-l-primary text-primary font-medium"
                    : "border-l-line text-zinc-400 hover:text-white hover:border-l-zinc-600"
                }`}
              >
                <span
                  className={`text-xs font-mono shrink-0 ${
                    active ? "text-primary" : "text-zinc-500"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="leading-snug">{h.text}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
